import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { API_BASE } from "../../constants/api";


const PROVIDERS = [
  { key: "Square", enabledField: "square_enabled", description: "Square payments, terminals and devices" },
  { key: "Stripe", enabledField: "stripe_enabled", description: "Stripe Connect payments and terminals" },
];

export default function PaymentMethodSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [defaultProvider, setDefaultProvider] = useState("Stripe");
  const [squareEnabled, setSquareEnabled] = useState(true);
  const [stripeEnabled, setStripeEnabled] = useState(true);
  const [tenantCounts, setTenantCounts] = useState(null);

  const enabledByField = {
    square_enabled: squareEnabled,
    stripe_enabled: stripeEnabled,
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API_BASE}/admin/payments/global-settings`, {
        credentials: "include",
      });
      const j = await r.json();
      if (!r.ok || j.success === false) throw new Error(j.error || "Failed to load");
      setDefaultProvider(j.settings?.default_provider || "Stripe");
      setSquareEnabled(j.settings?.square_enabled !== false);
      setStripeEnabled(j.settings?.stripe_enabled !== false);
      setTenantCounts(j.tenantCounts || null);
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleProvider = (provider, checked) => {
    const otherEnabled = provider === "Square" ? stripeEnabled : squareEnabled;
    if (!checked && !otherEnabled) {
      toast.error("At least one payment provider must remain enabled");
      return;
    }
    if (provider === "Square") setSquareEnabled(checked);
    else setStripeEnabled(checked);
    if (!checked && defaultProvider === provider) {
      const other = provider === "Square" ? "Stripe" : "Square";
      setDefaultProvider(other);
      toast(`Default payment method switched to ${other}`, { icon: "ℹ️" });
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const body = {
        default_provider: defaultProvider,
        square_enabled: squareEnabled,
        stripe_enabled: stripeEnabled,
      };
      const r = await fetch(`${API_BASE}/admin/payments/global-settings`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok || j.success === false) throw new Error(j.error || "Save failed");
      toast.success("Payment method settings updated");
      setDefaultProvider(j.settings?.default_provider || "Stripe");
      setSquareEnabled(j.settings?.square_enabled !== false);
      setStripeEnabled(j.settings?.stripe_enabled !== false);
    } catch (e) {
      toast.error(e.message || "Save failed");
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const tenantCountFor = (provider) => {
    if (!tenantCounts) return null;
    const own = provider === "Square" ? tenantCounts.square_only : tenantCounts.stripe_only;
    return `${own ?? 0} tenants use only ${provider} · ${tenantCounts.both ?? 0} use both`;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Toaster position="top-right" />
      <Typography variant="h5" sx={{ mb: 2 }} fontWeight={700}>
        Payment methods
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Controls Square and Stripe for all tenants globally. The default method applies to every tenant,
        and disabling a method makes it immediately unavailable to all tenants.
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <CardContent>
          {loading ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={32} />
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              {PROVIDERS.map(({ key, enabledField, description }) => (
                <Stack
                  key={key}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontWeight={600}>{key}</Typography>
                      {defaultProvider === key ? (
                        <Chip label="Default" color="primary" size="small" />
                      ) : null}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {description}
                    </Typography>
                    {tenantCounts ? (
                      <Typography variant="caption" color="text.secondary">
                        {tenantCountFor(key)}
                      </Typography>
                    ) : null}
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabledByField[enabledField]}
                        onChange={(e) => toggleProvider(key, e.target.checked)}
                      />
                    }
                    label={enabledByField[enabledField] ? "Enabled" : "Disabled"}
                    labelPlacement="start"
                  />
                </Stack>
              ))}

              <Divider />

              <Box>
                <Typography fontWeight={600} sx={{ mb: 1 }}>
                  Default payment method
                </Typography>
                <RadioGroup
                  row
                  value={defaultProvider}
                  onChange={(e) => setDefaultProvider(e.target.value)}
                >
                  {PROVIDERS.map(({ key, enabledField }) => (
                    <FormControlLabel
                      key={key}
                      value={key}
                      control={<Radio />}
                      label={key}
                      disabled={!enabledByField[enabledField]}
                    />
                  ))}
                </RadioGroup>
                <Typography variant="caption" color="text.secondary">
                  Applied to all tenants and pre-selected when a tenant sets up payments.
                </Typography>
              </Box>

              <Box>
                <Button variant="contained" onClick={save} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
